import React, { useState, useRef, useEffect } from 'react';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [step, setStep] = useState('username'); // 'username' | 'pin_entry' | 'pin_setup'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pinRef = useRef(null);
  const pinConfirmRef = useRef(null);

  useEffect(() => {
    if (step !== 'username' && pinRef.current) {
      pinRef.current.focus();
    }
  }, [step]);

  const submitUsername = async (e) => {
    e.preventDefault();
    if (!username.trim()) { setError('Please enter a username'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (data.requires_pin_setup) { setStep('pin_setup'); }
      else if (data.requires_pin) { setStep('pin_entry'); }
      else { setError(data.error || 'Login failed. Please try again.'); }
    } catch { setError('Error connecting to server.'); }
    setLoading(false);
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError('PIN must be exactly 4 digits'); return;
    }
    if (step === 'pin_setup') {
      if (pinConfirm !== pin) { setError('PINs do not match'); return; }
    }
    setLoading(true);
    setError('');
    try {
      const body = { username: username.trim(), pin };
      if (step === 'pin_setup') body.pin_confirm = pinConfirm;
      const res = await fetch(`${API_URL}/api/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.session_token) {
        onLogin(data);
      } else {
        setError(data.error || 'Incorrect PIN. Please try again.');
      }
    } catch { setError('Error connecting to server.'); }
    setLoading(false);
  };

  const goBack = () => {
    setStep('username');
    setPin('');
    setPinConfirm('');
    setError('');
  };

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Daily Logger</h2>

        {step === 'username' && (
          <>
            <p>Enter your username to access your logs</p>
            <form onSubmit={submitUsername}>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="username-input"
                  autoComplete="username"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          </>
        )}

        {(step === 'pin_entry' || step === 'pin_setup') && (
          <div className="password-modal-overlay">
            <div className="password-modal">
              {step === 'pin_setup' ? (
                <>
                  <h3>Create Your PIN</h3>
                  <p>Choose a 4-digit PIN for <strong>{username}</strong></p>
                </>
              ) : (
                <>
                  <h3>Enter Your PIN</h3>
                  <p>Welcome back, <strong>{username}</strong></p>
                </>
              )}

              <form onSubmit={submitPin}>
                <input
                  ref={pinRef}
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  pattern="[0-9]*"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="• • • •"
                  className="password-input pin-input"
                  autoComplete="current-password"
                />
                {step === 'pin_setup' && (
                  <input
                    ref={pinConfirmRef}
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    pattern="[0-9]*"
                    value={pinConfirm}
                    onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Confirm PIN"
                    className="password-input pin-input"
                    autoComplete="new-password"
                    style={{ marginTop: '10px' }}
                  />
                )}
                {error && <div className="error-message">{error}</div>}
                <div className="password-modal-buttons">
                  <button type="button" className="cancel-button" onClick={goBack}>
                    Back
                  </button>
                  <button type="submit" className="login-button" disabled={loading}>
                    {loading ? 'Verifying...' : step === 'pin_setup' ? 'Set PIN' : 'Login'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
